import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, ScrollText, ArrowLeft, Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "../lib/api";
import { LegalContent } from "../lib/legalRender";

const TABS = [
  { key: "privacy", label: "Privacy Policy", icon: Shield, field: "privacy_policy" },
  { key: "terms", label: "Terms of Use", icon: ScrollText, field: "terms_of_use" },
];

export default function LegalPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = searchParams.get("tab") === "terms" ? "terms" : "privacy";
  const [tab, setTab] = useState(initial);
  const [data, setData] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/legal").then((r) => setData(r.data)).catch(() => setData({ privacy_policy: "", terms_of_use: "" }));
  }, []);

  const active = TABS.find((t) => t.key === tab);
  const content = data ? (data[active.field] || "") : null;
  const updated = data?.updated_at ? new Date(data.updated_at) : null;

  const pickTab = (k) => { setTab(k); setSearchParams(k === "terms" ? { tab: "terms" } : {}, { replace: true }); };

  return (
    <div className="grain" style={{ minHeight: "100dvh", background: "var(--bg-base)", color: "var(--text-primary)" }} data-testid="legal-page">
      {/* Ambient gold glow */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden>
        <div className="absolute -top-40 -right-32 h-96 w-96 rounded-full"
          style={{ background: "radial-gradient(circle, color-mix(in srgb, var(--accent) 22%, transparent), transparent 70%)", filter: "blur(40px)" }} />
        <div className="absolute -bottom-48 -left-32 h-96 w-96 rounded-full"
          style={{ background: "radial-gradient(circle, color-mix(in srgb, var(--accent) 14%, transparent), transparent 70%)", filter: "blur(40px)" }} />
      </div>

      {/* Header — matches the landing page nav position */}
      <header className="sticky top-0 z-30 glass" style={{ borderBottom: "1px solid var(--border)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center gap-3">
          <button onClick={() => navigate("/")} data-testid="legal-back-btn"
            className="h-9 w-9 rounded-full grid place-items-center shrink-0" style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)" }}>
            <ArrowLeft size={18} style={{ color: "var(--text-secondary)" }} />
          </button>
          <span className="font-serif-display text-xl" style={{ color: "var(--text-primary)" }}>The Family Vault</span>
        </div>
      </header>

      <main className="relative max-w-4xl mx-auto px-5 lg:px-8 pt-8 lg:pt-14 pb-24">
        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="text-center mb-8">
          <div className="h-16 w-16 mx-auto rounded-2xl grid place-items-center gold-border mb-4"
            style={{ background: "var(--bg-surface)", boxShadow: "0 0 28px var(--shell-glow, rgba(124,131,255,0.35))" }}>
            <Shield size={30} className="gold-text" />
          </div>
          <span className="text-[11px] uppercase tracking-[0.25em]" style={{ color: "var(--text-muted)" }}>Trust &amp; Transparency</span>
          <h1 className="font-serif-display text-4xl sm:text-5xl leading-tight mt-2">The fine print, in plain sight.</h1>
          <p className="text-sm mt-3 max-w-md mx-auto" style={{ color: "var(--text-secondary)" }}>
            How we protect your family's data and the terms you agree to when using the vault.
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2 p-1 rounded-2xl mb-6 max-w-md mx-auto" style={{ background: "var(--overlay)", border: "1px solid var(--border)" }}>
          {TABS.map((t) => (
            <button key={t.key} onClick={() => pickTab(t.key)} data-testid={`legal-tab-${t.key}`}
              className="flex-1 rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors relative"
              style={{ color: tab === t.key ? "#fff" : "var(--text-secondary)" }}>
              {tab === t.key && (
                <motion.div layoutId="legal-tab-pill" className="absolute inset-0 rounded-xl" style={{ background: "var(--accent)" }} transition={{ type: "spring", stiffness: 400, damping: 34 }} />
              )}
              <span className="relative flex items-center gap-1.5"><t.icon size={15} /> {t.label}</span>
            </button>
          ))}
        </div>

        {/* Content card */}
        <motion.div layout className="rounded-3xl p-6 lg:p-9 relative overflow-hidden"
          style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", boxShadow: "0 20px 60px rgba(0,0,0,0.35)" }}>
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, color-mix(in srgb, var(--accent) 60%, transparent), transparent)" }} />

          {data === null ? (
            <div className="grid place-items-center py-16"><Loader2 className="animate-spin gold-text" /></div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                {content && content.trim() ? (
                  <>
                    <LegalContent className="vault-md legal-md" content={content} data-testid="legal-content" style={{ color: "var(--text-secondary)" }} />
                    {updated && (
                      <div className="text-[11px] mt-8 pt-5" style={{ color: "var(--text-muted)", borderTop: "1px solid var(--border)" }}>
                        Last updated {updated.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-14" data-testid="legal-empty">
                    <ScrollText size={26} className="mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
                    <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                      This {active.label.toLowerCase()} hasn't been published yet. Please check back soon.
                    </p>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          )}
        </motion.div>

        <p className="text-center text-xs mt-10" style={{ color: "var(--text-muted)" }}>
          © {new Date().getFullYear()} The Family Vault · Your family, your data.
        </p>
      </main>
    </div>
  );
}
